/**
 * The book cipher: a message is written as positions in a text both people hold.
 *
 * Two choices define a scheme.
 *
 * - **Unit** — what a position points at. `word` points at a whole word, so a plain
 *   word becomes the position of the same word in the book. `first-letter` points at a
 *   word too, but only its first letter is used (the method of the Beale ciphers), so
 *   any letter that starts a word somewhere in the book can be written. `letter` points
 *   at a single letter anywhere in the text.
 * - **Address** — how a position is counted. `number` is one running count from the
 *   start of the book. `line` is `line:n`, counting lines through the whole text.
 *   `page` is `page:line:n`, where a line starting with `---` ends a page and line
 *   numbers restart.
 *
 * Words are runs of letters and digits, with an apostrophe allowed inside ("don't" is
 * one word); a hyphen ends a word. Lines with no words are not numbered. Matching
 * ignores case and accents. Counting is by letters, not by code unit.
 */

export type BookUnit = "word" | "first-letter" | "letter";
export type BookAddress = "number" | "line" | "page";
export type BookPick = "first" | "rotate";

export const MISSING_MARK = "?";
export const WORD_BREAK_MARK = "/";

const WORD_PATTERN = /[\p{L}\p{N}]+(?:['’][\p{L}\p{N}]+)*/gu;
const LETTER_PATTERN = /\p{L}/gu;
const MARKS = /\p{M}/gu;
const LINE_BREAKS = /\r\n|\r|\n|\f/;
const PAGE_MARKER = /^\s*---+\s*$/;
const REF_SPLIT = /[\s,;]+/;
const REF_PART_SPLIT = /[:.-]/;
const DIGITS = /^\d+$/;

/** Case- and accent-insensitive form used for matching. */
export function foldForMatch(text: string): string {
  return text.normalize("NFD").replace(MARKS, "").toLowerCase();
}

/** One addressable place in the key text. */
export interface BookEntry {
  /** The word it sits in (for `letter`, the word holding the letter). */
  word: string;
  /** The word, or the single letter, this entry stands for. */
  text: string;
  /** Matching key: the folded word, or the folded letter. */
  match: string;
  page: number;
  /** Line within its page (restarts at 1 after a page marker). */
  lineInPage: number;
  /** Line counted through the whole text. */
  line: number;
  /** Position within the line, counted in the entry's unit. */
  inLine: number;
  /** Position in the whole text, counted in the entry's unit. */
  overall: number;
}

interface ParsedWord {
  text: string;
  page: number;
  lineInPage: number;
  line: number;
  inLine: number;
}

/** Every word of the key text with its page, line and place in the line. */
export function parseKeyWords(keyText: string): ParsedWord[] {
  const words: ParsedWord[] = [];
  let page = 1;
  let lineInPage = 0;
  let line = 0;
  for (const rawLine of keyText.split(LINE_BREAKS)) {
    if (PAGE_MARKER.test(rawLine)) {
      page += 1;
      lineInPage = 0;
      continue;
    }
    const found = rawLine.match(WORD_PATTERN) ?? [];
    if (found.length === 0) {
      continue;
    }
    lineInPage += 1;
    line += 1;
    for (const [index, text] of found.entries()) {
      words.push({ text, page, lineInPage, line, inLine: index + 1 });
    }
  }
  return words;
}

function wordEntries(words: readonly ParsedWord[]): BookEntry[] {
  return words.map((word, index) => ({
    word: word.text,
    text: word.text,
    match: foldForMatch(word.text),
    page: word.page,
    lineInPage: word.lineInPage,
    line: word.line,
    inLine: word.inLine,
    overall: index + 1,
  }));
}

function firstLetterEntries(words: readonly ParsedWord[]): BookEntry[] {
  return wordEntries(words).map((entry) => {
    const first = [...(entry.word.match(LETTER_PATTERN) ?? [])].at(0) ?? "";
    return { ...entry, text: first, match: foldForMatch(first) };
  });
}

function letterEntries(words: readonly ParsedWord[]): BookEntry[] {
  const entries: BookEntry[] = [];
  let lastLine = 0;
  let inLine = 0;
  for (const word of words) {
    if (word.line !== lastLine) {
      lastLine = word.line;
      inLine = 0;
    }
    for (const letter of word.text.match(LETTER_PATTERN) ?? []) {
      inLine += 1;
      entries.push({
        word: word.text,
        text: letter,
        match: foldForMatch(letter),
        page: word.page,
        lineInPage: word.lineInPage,
        line: word.line,
        inLine,
        overall: entries.length + 1,
      });
    }
  }
  return entries;
}

/** Every place the chosen unit can point at, in reading order. */
export function bookEntries(keyText: string, unit: BookUnit): BookEntry[] {
  const words = parseKeyWords(keyText);
  if (unit === "letter") {
    return letterEntries(words);
  }
  return unit === "first-letter"
    ? firstLetterEntries(words)
    : wordEntries(words);
}

/** The reference text for an entry: `12`, `3:5` or `1:3:5`. */
export function formatRef(entry: BookEntry, address: BookAddress): string {
  if (address === "number") {
    return String(entry.overall);
  }
  if (address === "line") {
    return `${entry.line}:${entry.inLine}`;
  }
  return `${entry.page}:${entry.lineInPage}:${entry.inLine}`;
}

/** The parts count each address expects. */
const REF_PARTS: Record<BookAddress, number> = { number: 1, line: 2, page: 3 };

function normaliseRef(ref: string, address: BookAddress): string | null {
  const parts = ref.split(REF_PART_SPLIT);
  if (parts.length !== REF_PARTS[address]) {
    return null;
  }
  if (!parts.every((part) => DIGITS.test(part))) {
    return null;
  }
  return parts.map(Number).join(":");
}

/** One step of encoding. */
export interface BookEncodeRow {
  /** What was looked up: a word, a letter, or the break marker. */
  plain: string;
  /** The reference written, or `null` when the book cannot supply it. */
  ref: string | null;
  entry: BookEntry | null;
  isBreak: boolean;
}

export interface BookOptions {
  unit: BookUnit;
  address: BookAddress;
  pick: BookPick;
  /** Letter units: write `/` between words so the decoder can restore spaces. */
  wordBreaks: boolean;
}

export const DEFAULT_BOOK_OPTIONS: BookOptions = {
  unit: "first-letter",
  address: "number",
  pick: "first",
  wordBreaks: true,
};

function plainTokens(
  plain: string,
  unit: BookUnit,
  wordBreaks: boolean
): { token: string; isBreak: boolean }[] {
  const tokens: { token: string; isBreak: boolean }[] = [];
  if (unit === "word") {
    for (const word of plain.match(WORD_PATTERN) ?? []) {
      tokens.push({ token: word, isBreak: false });
    }
    return tokens;
  }
  const words = plain.match(WORD_PATTERN) ?? [];
  for (const [index, word] of words.entries()) {
    if (index > 0 && wordBreaks) {
      tokens.push({ token: WORD_BREAK_MARK, isBreak: true });
    }
    for (const letter of word.match(LETTER_PATTERN) ?? []) {
      tokens.push({ token: letter, isBreak: false });
    }
  }
  return tokens;
}

function groupByMatch(entries: readonly BookEntry[]): Map<string, BookEntry[]> {
  const groups = new Map<string, BookEntry[]>();
  for (const entry of entries) {
    const list = groups.get(entry.match);
    if (list) {
      list.push(entry);
    } else {
      groups.set(entry.match, [entry]);
    }
  }
  return groups;
}

export interface BookEncodeResult {
  rows: BookEncodeRow[];
  /** The cipher text: references separated by spaces. */
  output: string;
  /** Words or letters the book could not supply, each once. */
  missing: string[];
}

export function bookEncode(
  plain: string,
  keyText: string,
  options: BookOptions
): BookEncodeResult {
  const groups = groupByMatch(bookEntries(keyText, options.unit));
  const used = new Map<string, number>();
  const rows: BookEncodeRow[] = [];
  const missing = new Set<string>();
  for (const { token, isBreak } of plainTokens(
    plain,
    options.unit,
    options.wordBreaks
  )) {
    if (isBreak) {
      rows.push({ plain: token, ref: WORD_BREAK_MARK, entry: null, isBreak });
      continue;
    }
    const key = foldForMatch(token);
    const choices = groups.get(key) ?? [];
    if (choices.length === 0) {
      missing.add(token);
      rows.push({ plain: token, ref: null, entry: null, isBreak });
      continue;
    }
    const turn = used.get(key) ?? 0;
    used.set(key, turn + 1);
    const entry =
      options.pick === "rotate" ? choices[turn % choices.length] : choices[0];
    rows.push({
      plain: token,
      ref: formatRef(entry, options.address),
      entry,
      isBreak,
    });
  }
  const output = rows.map((row) => row.ref ?? MISSING_MARK).join(" ");
  return { rows, output, missing: [...missing] };
}

export interface BookDecodeRow {
  /** The reference as read, normalised (`03:5` becomes `3:5`). */
  ref: string;
  entry: BookEntry | null;
  isBreak: boolean;
}

export interface BookDecodeResult {
  rows: BookDecodeRow[];
  output: string;
  /** References that do not exist in this book or this address style. */
  invalid: string[];
}

export function bookDecode(
  cipher: string,
  keyText: string,
  options: Pick<BookOptions, "unit" | "address">
): BookDecodeResult {
  const lookup = new Map<string, BookEntry>();
  for (const entry of bookEntries(keyText, options.unit)) {
    lookup.set(formatRef(entry, options.address), entry);
  }
  const rows: BookDecodeRow[] = [];
  const invalid: string[] = [];
  for (const raw of cipher.split(REF_SPLIT)) {
    if (raw.length === 0) {
      continue;
    }
    if (raw === WORD_BREAK_MARK) {
      rows.push({ ref: raw, entry: null, isBreak: true });
      continue;
    }
    const ref = normaliseRef(raw, options.address);
    const entry = ref === null ? null : (lookup.get(ref) ?? null);
    if (entry === null) {
      invalid.push(raw);
    }
    rows.push({ ref: ref ?? raw, entry, isBreak: false });
  }
  const separator = options.unit === "word" ? " " : "";
  const pieces = rows.map((row) => {
    if (row.isBreak) {
      return " ";
    }
    if (row.entry === null) {
      return MISSING_MARK;
    }
    return options.unit === "word"
      ? row.entry.text.toLowerCase()
      : row.entry.text.toUpperCase();
  });
  return { rows, output: pieces.join(separator), invalid };
}
